import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { 
  Users, TrendingUp, Phone, Mail, Car, Calendar, 
  Search, Filter, Download, Eye, Edit2, LogOut,
  BarChart3, Activity, DollarSign, Clock
} from 'lucide-react';
import axios from 'axios';
import AnalyticsDashboard from './AnalyticsDashboard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = ({ defaultTab = 'dashboard' }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState(localStorage.getItem('admin_token'));
  const [loginData, setLoginData] = useState({ username: '', password: '' });
  const [isLoading, setIsLoading] = useState(false);
  
  // Dashboard data
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [filters, setFilters] = useState({
    status: 'all',
    model: '',
    search: ''
  });
  const [selectedLead, setSelectedLead] = useState(null);
  const [activeTab, setActiveTab] = useState(defaultTab); // 'dashboard', 'analytics'

  useEffect(() => {
    if (token) {
      setIsAuthenticated(true);
      fetchDashboardData();
    }
  }, [token]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/admin/login`, loginData);
      const { access_token } = response.data;
      
      localStorage.setItem('admin_token', access_token);
      setToken(access_token);
      setIsAuthenticated(true);
      
      toast.success('Login berjaya!');
    } catch (error) {
      toast.error('Username atau password salah');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    setToken(null);
    setIsAuthenticated(false);
    setStats(null);
    setLeads([]);
    setInquiries([]);
  };

  const fetchDashboardData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      // Fetch dashboard stats
      const statsResponse = await axios.get(`${API}/admin/dashboard`, { headers });
      setStats(statsResponse.data);
      
      // Fetch leads
      const leadsResponse = await axios.get(`${API}/admin/leads`, { headers });
      setLeads(leadsResponse.data);
      
      // Fetch inquiries
      const inquiriesResponse = await axios.get(`${API}/admin/inquiries`, { headers });
      setInquiries(inquiriesResponse.data);
      
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      if (error.response?.status === 401) {
        handleLogout();
      }
    }
  };

  const updateLeadStatus = async (leadId, status, notes = '') => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/admin/lead/${leadId}`, { status, notes }, { headers });
      
      toast.success('Status lead dikemaskini');
      fetchDashboardData();
      setSelectedLead(null);
    } catch (error) {
      toast.error('Gagal kemaskini status');
    }
  };

  const exportLeads = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/admin/export/leads`, { 
        headers,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'leads.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Data leads berjaya diexport');
    } catch (error) {
      toast.error('Gagal export data');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-blue-100 text-blue-800';
      case 'contacted': return 'bg-yellow-100 text-yellow-800';
      case 'interested': return 'bg-purple-100 text-purple-800';
      case 'quoted': return 'bg-orange-100 text-orange-800';
      case 'converted': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-MY', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Login Form
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 to-indigo-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-2xl">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-bold text-slate-800">
              Syasyah Admin Dashboard
            </CardTitle>
            <p className="text-slate-600">Login untuk akses admin panel</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label>Username</Label>
                <Input
                  type="text"
                  value={loginData.username}
                  onChange={(e) => setLoginData({...loginData, username: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label>Password</Label>
                <Input
                  type="password"
                  value={loginData.password}
                  onChange={(e) => setLoginData({...loginData, password: e.target.value})}
                  required
                />
              </div>
              <Button 
                type="submit" 
                className="w-full" 
                disabled={isLoading}
              >
                {isLoading ? 'Logging in...' : 'Login'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Main Dashboard
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <h1 className="text-xl font-bold text-gray-900">
              Syasyah Perodua Admin
            </h1>
            <Button 
              onClick={handleLogout}
              variant="outline"
              className="flex items-center space-x-2"
            >
              <LogOut className="w-4 h-4" />
              <span>Logout</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Tab Navigation - Mobile Responsive */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
        <div className="border-b border-gray-200 bg-white">
          <nav className="-mb-px flex space-x-2 sm:space-x-8 overflow-x-auto">
            <button
              onClick={() => setActiveTab('dashboard')}
              className={`whitespace-nowrap py-3 px-3 sm:px-1 border-b-2 font-medium text-xs sm:text-sm flex items-center justify-center min-w-0 flex-shrink-0 ${
                activeTab === 'dashboard'
                  ? 'border-blue-500 text-blue-600 bg-blue-50'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 hover:bg-gray-50'
              }`}
            >
              <Users className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden xs:inline">Dashboard & Leads</span>
              <span className="xs:hidden">Leads</span>
            </button>
            <button
              onClick={() => setActiveTab('analytics')}
              className={`whitespace-nowrap py-3 px-3 sm:px-1 border-b-2 font-medium text-xs sm:text-sm flex items-center justify-center min-w-0 flex-shrink-0 ${
                activeTab === 'analytics'
                  ? 'border-blue-500 text-blue-600 bg-blue-50'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 hover:bg-gray-50'
              }`}
            >
              <BarChart3 className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden xs:inline">Website Analytics</span>
              <span className="xs:hidden">Analytics</span>
            </button>
          </nav>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'dashboard' ? (
          <>
            {/* Stats Cards */}
            {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Total Leads</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.total_leads}</p>
                  </div>
                  <Users className="w-8 h-8 text-blue-600" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">New Leads</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.new_leads}</p>
                  </div>
                  <Activity className="w-8 h-8 text-green-600" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Converted</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.converted_leads}</p>
                  </div>
                  <DollarSign className="w-8 h-8 text-purple-600" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Conversion Rate</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.conversion_rate}%</p>
                  </div>
                  <BarChart3 className="w-8 h-8 text-orange-600" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters & Export */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Leads Management</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4 mb-4">
              <div className="flex-1 min-w-64">
                <Input
                  placeholder="Search by name, phone, email..."
                  value={filters.search}
                  onChange={(e) => setFilters({...filters, search: e.target.value})}
                  className="w-full"
                />
              </div>
              <Select value={filters.status} onValueChange={(value) => setFilters({...filters, status: value === 'all' ? '' : value})}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="contacted">Contacted</SelectItem>
                  <SelectItem value="interested">Interested</SelectItem>
                  <SelectItem value="quoted">Quoted</SelectItem>
                  <SelectItem value="converted">Converted</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={exportLeads} className="flex items-center space-x-2">
                <Download className="w-4 h-4" />
                <span>Export CSV</span>
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Leads Table */}
        <Card>
          <CardHeader>
            <CardTitle>Recent Leads ({leads.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-2">Name</th>
                    <th className="text-left p-2">Contact</th>
                    <th className="text-left p-2">Model</th>
                    <th className="text-left p-2">Status</th>
                    <th className="text-left p-2">Date</th>
                    <th className="text-left p-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((lead) => (
                    <tr key={lead.id} className="border-b hover:bg-gray-50">
                      <td className="p-2 font-medium">{lead.name}</td>
                      <td className="p-2">
                        <div className="space-y-1">
                          <div className="flex items-center space-x-1">
                            <Phone className="w-3 h-3" />
                            <span>{lead.phone}</span>
                          </div>
                          {lead.email && (
                            <div className="flex items-center space-x-1">
                              <Mail className="w-3 h-3" />
                              <span>{lead.email}</span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="p-2">
                        {lead.interested_model && (
                          <div className="flex items-center space-x-1">
                            <Car className="w-3 h-3" />
                            <span>{lead.interested_model} {lead.variant}</span>
                          </div>
                        )}
                      </td>
                      <td className="p-2">
                        <Badge className={getStatusColor(lead.status)}>
                          {lead.status}
                        </Badge>
                      </td>
                      <td className="p-2">
                        <div className="flex items-center space-x-1 text-xs text-gray-500">
                          <Calendar className="w-3 h-3" />
                          <span>{formatDate(lead.created_at)}</span>
                        </div>
                      </td>
                      <td className="p-2">
                        <div className="flex space-x-2">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => setSelectedLead(lead)}
                          >
                            <Eye className="w-3 h-3" />
                          </Button>
                          <Button 
                            size="sm" 
                            onClick={() => window.open(`https://wa.me/6${lead.phone.replace(/[^0-9]/g, '')}`, '_blank')}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            <Phone className="w-3 h-3" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Lead Detail Modal */}
        {selectedLead && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle>Lead Details - {selectedLead.name}</CardTitle>
                  <Button onClick={() => setSelectedLead(null)} variant="outline">
                    Close
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Name</Label>
                    <p className="font-medium">{selectedLead.name}</p>
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <p className="font-medium">{selectedLead.phone}</p>
                  </div>
                  <div>
                    <Label>Email</Label>
                    <p className="font-medium">{selectedLead.email || 'N/A'}</p>
                  </div>
                  <div>
                    <Label>Status</Label>
                    <Badge className={getStatusColor(selectedLead.status)}>
                      {selectedLead.status}
                    </Badge>
                  </div>
                  <div>
                    <Label>Interested Model</Label>
                    <p className="font-medium">{selectedLead.interested_model || 'N/A'}</p>
                  </div>
                  <div>
                    <Label>Variant</Label>
                    <p className="font-medium">{selectedLead.variant || 'N/A'}</p>
                  </div>
                  <div>
                    <Label>Budget Range</Label>
                    <p className="font-medium">{selectedLead.budget_range || 'N/A'}</p>
                  </div>
                  <div>
                    <Label>Deposit Amount</Label>
                    <p className="font-medium">{selectedLead.deposit_amount ? `RM${selectedLead.deposit_amount.toLocaleString()}` : 'N/A'}</p>
                  </div>
                </div>
                
                {selectedLead.message && (
                  <div>
                    <Label>Message</Label>
                    <p className="p-2 bg-gray-50 rounded">{selectedLead.message}</p>
                  </div>
                )}

                <div>
                  <Label>Update Status</Label>
                  <div className="flex space-x-2 mt-2">
                    <Button onClick={() => updateLeadStatus(selectedLead.id, 'contacted')} size="sm">
                      Mark Contacted
                    </Button>
                    <Button onClick={() => updateLeadStatus(selectedLead.id, 'interested')} size="sm" variant="outline">
                      Mark Interested
                    </Button>
                    <Button onClick={() => updateLeadStatus(selectedLead.id, 'converted')} size="sm" className="bg-green-600">
                      Mark Converted
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
          </>
        ) : (
          <AnalyticsDashboard token={token} />
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;